/**
 * timetableColumns — ScheduleDraft Timetable 只读表格与可编辑 Grid 列定义层。
 *
 * 本模块集中声明 Timetable 的展示列、单元格 renderer/editor 适配器与行级操作
 * 列。它只消费 row model 中的字段和 cell adapter，不持有行状态或调用数据
 * 查询。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → components/editor/timetable/timetableRowModel.js (行字段), components/editor/timetable-cells/ (单元格适配器), components/editor/timetable/ScheduleDraftTimetableSection.jsx (列消费)
 */

import React from "react";
import { CalculatorOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Modal, Popover, Space, Tag, Tooltip, Typography } from "antd";
import { textEditor } from "react-data-grid";
import FlightTimeDisplay from "../../../../../../components/DateTimeDisplay/FlightTimeDisplay";
import "../../../../../../components/common/EditableCellGrid/EditableCellGrid.css";
import { formatMinutesAsHHMM, normalizeMinutes } from "../../../lib/flightTimingUtils";
import AircraftCellEditor from "../timetable-cells/AircraftCellEditor";
import AircraftCellRenderer from "../timetable-cells/AircraftCellRenderer";
import AirportCellEditor from "../timetable-cells/AirportCellEditor";
import AirportCellRenderer from "../timetable-cells/AirportCellRenderer";
import DayOfWeekCellRenderer from "../timetable-cells/DayOfWeekCellRenderer";
import FlightDateCellRenderer from "../timetable-cells/FlightDateCellRenderer";
import FlightNumberCellEditor from "../timetable-cells/FlightNumberCellEditor";
import FlightTimeCellEditor from "../timetable-cells/FlightTimeCellEditor";
import FlightTimeCellRenderer from "../timetable-cells/FlightTimeCellRenderer";
import RemarksCellRenderer from "../timetable-cells/RemarksCellRenderer";
import RouteCellRenderer from "../timetable-cells/RouteCellRenderer";
import STCCellEditor from "../timetable-cells/STCCellEditor";
import TimeBarCell from "../timetable-cells/TimeBarCell";
import { formatBlockTime } from "./timetableRowModel";

const { Text } = Typography;

const centerCellStyle = { display: "block", textAlign: "center" };
const TIMETABLE_CENTER_HEADER_CELL_CLASS = "schedule-draft-rdg-header-align-center";
const TIMETABLE_CENTER_CELL_CLASS = "schedule-draft-rdg-cell-align-center";
const EDITABLE_TIME_COLUMN_KEYS = new Set(["stdTime", "staTime"]);
const READONLY_TIME_COLUMN_KEYS = new Set(["std", "sta"]);

function joinClassNames(...classNames) {
    return classNames.filter(Boolean).join(" ") || undefined;
}

function resolveCellClass(cellClass, row) {
    return typeof cellClass === "function" ? cellClass(row) : cellClass;
}

function applyEditableColumnAlignment(column) {
    if (EDITABLE_TIME_COLUMN_KEYS.has(column.key)) {
        return column;
    }

    const originalCellClass = column.cellClass;
    return {
        ...column,
        headerCellClass: joinClassNames(column.headerCellClass, TIMETABLE_CENTER_HEADER_CELL_CLASS),
        cellClass: typeof originalCellClass === "function"
            ? (row) => joinClassNames(resolveCellClass(originalCellClass, row), TIMETABLE_CENTER_CELL_CLASS)
            : joinClassNames(originalCellClass, TIMETABLE_CENTER_CELL_CLASS),
    };
}

function applyReadonlyColumnAlignment(column) {
    if (READONLY_TIME_COLUMN_KEYS.has(column.key)) {
        return column;
    }

    return {
        ...column,
        align: "center",
    };
}

const renderAircraftEditCell = (props) => <AircraftCellEditor {...props} />;
const renderFlightNumberEditCell = (props) => <FlightNumberCellEditor {...props} />;
const renderAirportEditCell = (props) => <AirportCellEditor {...props} />;
const renderFlightTimeEditCell = (props) => <FlightTimeCellEditor {...props} />;
const renderSTCEditCell = (props) => <STCCellEditor {...props} />;

function RemarksCellEditor(props) {
    const closedRef = React.useRef(false);
    const closeEditor = React.useCallback((commitChanges, shouldFocusCell) => {
        if (closedRef.current) {
            return;
        }
        closedRef.current = true;
        props.onClose(commitChanges, shouldFocusCell);
    }, [props]);

    const editor = textEditor({ ...props, onClose: closeEditor });
    if (!React.isValidElement(editor)) {
        return editor;
    }

    return React.cloneElement(editor, {
        className: joinClassNames(editor.props.className, "editable-cell-editor schedule-draft-rdg-remarks-editor"),
        onKeyDown: (event) => {
            editor.props.onKeyDown?.(event);
            if (event.defaultPrevented) {
                return;
            }
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                closeEditor(false, false);
            }
        },
    });
}

const renderRemarksEditCell = (props) => <RemarksCellEditor {...props} />;
const LOCAL_CONSTRAINT_LABELS = {
    AIRPORT_DISCONTINUITY: "不连续",
    TIME_OVERLAP: "时间重叠",
    INSUFFICIENT_GROUND_TIME_LOCAL: "过站不足",
    MISSING_MIN_GROUND_TIME_LOCAL: "过站配置缺失",
    DEP_CURFEW_LOCAL: "宵禁违规",
    ARR_CURFEW_LOCAL: "宵禁违规",
    DEP_CURFEW_NEAR_WARNING: "临近宵禁",
    DEP_CURFEW_NEAR_NOTICE: "临近宵禁",
    ARR_CURFEW_NEAR_WARNING: "临近宵禁",
    ARR_CURFEW_NEAR_NOTICE: "临近宵禁",
};
const BACKEND_CONSTRAINT_LABELS = {
    H2_CONNECTIVITY: "航段不连续",
    H2_TIME_OVERLAP: "时间重叠",
    H3_MIN_GROUND: "过站不足",
    H4_CURFEW: "宵禁违规",
    H4_NEAR_CURFEW: "临近宵禁",
    H5_MAINTENANCE: "飞机不可用",
    H6_ROUTE_LEGAL: "航路问题",
    AUTO_CALC_DELAY: "自动顺延",
    airport_discontinuity: "航段不连续",
    time_overlap: "时间重叠",
    route_legality: "航路问题",
    dep_curfew: "宵禁违规",
    arr_curfew: "宵禁违规",
    dep_curfew_near: "临近宵禁",
    arr_curfew_near: "临近宵禁",
    auto_curfew_delay: "自动顺延",
    auto_aircraft_availability_delay: "自动顺延",
    auto_calculation_delay: "自动顺延",
    insufficient_ground_time: "过站不足",
    ground_time: "过站不足",
    missing_turnaround_data: "过站配置缺失",
    aircraft_unavailable: "飞机不可用",
    aircraft_unavailable_near: "临近不可用窗口",
};
const CONSTRAINT_LABELS = {
    ...LOCAL_CONSTRAINT_LABELS,
    ...BACKEND_CONSTRAINT_LABELS,
};
const CONSTRAINT_SHORT_LABELS = {
    AIRPORT_DISCONTINUITY: "连续",
    H2_CONNECTIVITY: "连续",
    airport_discontinuity: "连续",
    TIME_OVERLAP: "重叠",
    H2_TIME_OVERLAP: "重叠",
    time_overlap: "重叠",
    INSUFFICIENT_GROUND_TIME_LOCAL: "过站",
    MISSING_MIN_GROUND_TIME_LOCAL: "过站",
    H3_MIN_GROUND: "过站",
    insufficient_ground_time: "过站",
    ground_time: "过站",
    missing_turnaround_data: "过站",
    DEP_CURFEW_LOCAL: "宵禁",
    ARR_CURFEW_LOCAL: "宵禁",
    DEP_CURFEW_NEAR_WARNING: "临近",
    DEP_CURFEW_NEAR_NOTICE: "临近",
    ARR_CURFEW_NEAR_WARNING: "临近",
    ARR_CURFEW_NEAR_NOTICE: "临近",
    H4_CURFEW: "宵禁",
    H4_NEAR_CURFEW: "临近",
    H5_MAINTENANCE: "飞机",
    dep_curfew: "宵禁",
    arr_curfew: "宵禁",
    dep_curfew_near: "临近",
    arr_curfew_near: "临近",
    H6_ROUTE_LEGAL: "航路",
    route_legality: "航路",
    aircraft_unavailable: "飞机",
    aircraft_unavailable_near: "临近",
    AUTO_CALC_DELAY: "顺延",
    auto_curfew_delay: "顺延",
    auto_aircraft_availability_delay: "顺延",
    auto_calculation_delay: "顺延",
};
const LOCAL_CONSTRAINT_TYPES = new Set(Object.keys(LOCAL_CONSTRAINT_LABELS));
const HARD_CONSTRAINT_TYPES = new Set(["H2_CONNECTIVITY", "H2_TIME_OVERLAP", "H3_MIN_GROUND", "H4_CURFEW", "H5_MAINTENANCE", "H6_ROUTE_LEGAL"]);
const WEEKDAY_LABELS = {
    MON: "周一",
    TUE: "周二",
    WED: "周三",
    THU: "周四",
    FRI: "周五",
    SAT: "周六",
    SUN: "周日",
};
const ISO_WEEKDAY_CODES = [null, "MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

function TimetableActionButton({ children, danger = false, disabled = false, title, onClick }) {
    return (
        <button
            type="button"
            className={`schedule-draft-rdg-action-button${danger ? " schedule-draft-rdg-action-button-danger" : ""}`}
            disabled={disabled}
            title={title}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (!disabled) {
                    onClick?.(event);
                }
            }}
        >
            {children}
        </button>
    );
}

function formatCurfewWindow(value) {
    if (!value) {
        return "未提供宵禁窗口";
    }

    if (typeof value === "string") {
        return value;
    }

    const start = value.start || value.start_time || value.from;
    const end = value.end || value.end_time || value.to;
    return start && end ? `${start}-${end}` : JSON.stringify(value);
}

function normalizeDisplayList(value) {
    if (value === undefined || value === null || value === "") {
        return [];
    }
    if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter(Boolean);
    }
    return String(value)
        .replace(/；/g, ";")
        .replace(/，/g, ",")
        .split(/[,;\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
}

function getRuleConditionObject(violation) {
    const candidates = [
        violation?.rule_conditions,
        violation?.ruleConditions,
        violation?.applicable_conditions,
        violation?.applicableConditions,
        violation?.conditions,
    ];
    return candidates.find((candidate) => candidate && typeof candidate === "object" && !Array.isArray(candidate)) || {};
}

function getRuleObject(violation) {
    const candidates = [violation?.rule, violation?.matched_rule, violation?.matchedRule, violation?.curfew_rule, violation?.curfewRule];
    return candidates.find((candidate) => candidate && typeof candidate === "object" && !Array.isArray(candidate)) || {};
}

function getConditionValue(violation, fields, { includeTopLevel = true } = {}) {
    const sources = [getRuleConditionObject(violation), getRuleObject(violation), includeTopLevel ? violation : null].filter(Boolean);
    for (const source of sources) {
        for (const field of fields) {
            if (source?.[field] !== undefined && source[field] !== null && source[field] !== "") {
                return source[field];
            }
        }
    }
    return null;
}

function formatWeekday(value) {
    if (Number.isFinite(Number(value))) {
        return WEEKDAY_LABELS[ISO_WEEKDAY_CODES[Number(value)]] || String(value);
    }
    const normalized = String(value || "").trim().slice(0, 3).toUpperCase();
    return WEEKDAY_LABELS[normalized] || String(value);
}

function formatOperation(value) {
    const normalized = String(value || "").trim().toLowerCase();
    if (normalized === "departure" || normalized === "dep") {
        return "起飞";
    }
    if (normalized === "arrival" || normalized === "arr") {
        return "到达";
    }
    return value ? String(value) : null;
}

function formatRuleConditionsLine(violation) {
    const parts = [];
    const operation = formatOperation(getConditionValue(violation, ["direction", "operation"]));
    const start = getConditionValue(violation, ["start", "start_time", "curfew_start"], { includeTopLevel: false });
    const end = getConditionValue(violation, ["end", "end_time", "curfew_end"], { includeTopLevel: false });
    const weekdays = normalizeDisplayList(getConditionValue(violation, ["weekdays", "applicable_weekdays", "applicableWeekdays", "days_of_week", "daysOfWeek"]));
    const dateStart = getConditionValue(violation, ["date_start", "dateStart", "valid_from", "validFrom"]);
    const dateEnd = getConditionValue(violation, ["date_end", "dateEnd", "valid_to", "validTo"]);
    const exceptions = normalizeDisplayList(getConditionValue(violation, ["aircraft_exceptions", "aircraftExceptions", "exceptions", "exception_aircraft", "exceptionAircraft"]));
    const ruleId = getConditionValue(violation, ["rule_id", "ruleId", "id", "code"]);
    const ruleName = getConditionValue(violation, ["rule_name", "ruleName", "name", "label"]);
    const ruleSource = getConditionValue(violation, ["rule_source", "ruleSource", "source"], { includeTopLevel: false })
        || violation?.rule_source
        || violation?.ruleSource;

    if (operation) {
        parts.push(`方向：${operation}`);
    }
    if (start || end) {
        parts.push(`窗口：${start || "--"}-${end || "--"}`);
    }
    if (weekdays.length) {
        parts.push(`星期：${weekdays.map(formatWeekday).join("、")}`);
    }
    if (dateStart || dateEnd) {
        parts.push(`日期范围：${dateStart || "不限"} 至 ${dateEnd || "不限"}`);
    }
    if (exceptions.length) {
        parts.push(`飞机例外：${exceptions.join("、")}`);
    }
    if (ruleName || ruleId) {
        parts.push(`规则：${[ruleName, ruleId].filter(Boolean).join(" / ")}`);
    }
    if (ruleSource) {
        parts.push(`规则来源：${ruleSource}`);
    }

    return parts.length ? `适用条件：${parts.join("；")}` : null;
}

function getConstraintViolationType(violation) {
    return violation?.type ?? violation?.constraint_type ?? violation?.violationType ?? violation?.code ?? null;
}

function isLocalConstraintViolation(violation) {
    const type = getConstraintViolationType(violation);
    return violation?.source === "client" || LOCAL_CONSTRAINT_TYPES.has(type);
}

function getConstraintSeverityClass(violation) {
    const severity = String(violation?.severity || "").toLowerCase();
    if (severity === "error") {
        return "schedule-draft-rdg-constraint-status-error";
    }
    if (severity === "warning") {
        return "schedule-draft-rdg-constraint-status-warn";
    }
    if (severity === "info") {
        return "schedule-draft-rdg-constraint-status-info";
    }
    return isLocalConstraintViolation(violation)
        ? "schedule-draft-rdg-constraint-status-warn"
        : "schedule-draft-rdg-constraint-status-error";
}

function getConstraintViolationLabel(violation) {
    const violationType = getConstraintViolationType(violation);
    return violation?.constraint_label || CONSTRAINT_LABELS[violationType] || (violation?.label ? String(violation.label) : null) || violationType || "约束违规";
}

function getConstraintStatusLabel(violation) {
    const violationType = getConstraintViolationType(violation);
    return violation?.status_label || CONSTRAINT_SHORT_LABELS[violationType] || CONSTRAINT_SHORT_LABELS[violation?.constraint_type] || getConstraintViolationLabel(violation);
}

function isGroundTimeViolationType(type) {
    return ["H3_MIN_GROUND", "insufficient_ground_time", "ground_time", "missing_turnaround_data", "INSUFFICIENT_GROUND_TIME_LOCAL", "MISSING_MIN_GROUND_TIME_LOCAL"].includes(type);
}

function isTimeOverlapViolationType(type) {
    return ["H2_TIME_OVERLAP", "time_overlap", "TIME_OVERLAP"].includes(type);
}

function isNearCurfewViolationType(type) {
    return ["H4_NEAR_CURFEW", "dep_curfew_near", "arr_curfew_near", "DEP_CURFEW_NEAR_WARNING", "DEP_CURFEW_NEAR_NOTICE", "ARR_CURFEW_NEAR_WARNING", "ARR_CURFEW_NEAR_NOTICE"].includes(type);
}

function isAutoCalculationDelayType(type) {
    return ["AUTO_CALC_DELAY", "auto_curfew_delay", "auto_aircraft_availability_delay", "auto_calculation_delay"].includes(type);
}

function isMissingGroundTimeConfigViolation(violation) {
    const type = getConstraintViolationType(violation);
    return type === "missing_turnaround_data" || type === "MISSING_MIN_GROUND_TIME_LOCAL";
}

function getConstraintSourceLabel(violation) {
    if (violation?.source === "client") {
        return "本地即时校验";
    }
    if (violation?.source === "timetable-row") {
        return "本地行计算";
    }
    if (violation?.source === "auto-calculate") {
        return "自动计算 preview";
    }
    return "后端 analysis";
}

function getConstraintSeverityLabel(violation) {
    const severity = String(violation?.severity || "").toLowerCase();
    const type = violation?.constraint_type || getConstraintViolationType(violation);
    const blocking = violation?.blocking === true || String(violation?.severity || "").toUpperCase() === "HARD" || (violation?.blocking !== false && HARD_CONSTRAINT_TYPES.has(type));
    if (blocking) {
        return "阻断";
    }
    if (severity === "error") {
        return "错误";
    }
    if (severity === "warning") {
        return "警告";
    }
    if (severity === "info") {
        return "提示";
    }
    return "需关注";
}

function getConstraintSeverityTagColor(violation) {
    const severity = String(violation?.severity || "").toLowerCase();
    const type = violation?.constraint_type || getConstraintViolationType(violation);
    if (violation?.blocking === true || String(violation?.severity || "").toUpperCase() === "HARD" || (violation?.blocking !== false && HARD_CONSTRAINT_TYPES.has(type))) {
        return "red";
    }
    if (severity === "error") {
        return "volcano";
    }
    if (severity === "warning") {
        return "gold";
    }
    if (severity === "info") {
        return "blue";
    }
    return "default";
}

function formatFlightSummary(flight) {
    if (!flight) {
        return null;
    }
    const flightNumber = flight.flightNumber || flight.flight_number || flight.id;
    const departure = flight.departureAirport || flight.departure_airport;
    const arrival = flight.arrivalAirport || flight.arrival_airport;
    return [flightNumber, departure && arrival ? `${departure}-${arrival}` : null].filter(Boolean).join(" ");
}

function formatFlightPair(row, violation) {
    const type = getConstraintViolationType(violation);
    if (isGroundTimeViolationType(type)) {
        const current = formatFlightSummary(row) || row?.id || violation?.previous_flight_id || violation?.prev_flight_id || violation?.flight_id;
        const next = formatFlightSummary(row?.nextFlightSummary) || violation?.next_flight_number || violation?.next_flight_id;
        return next ? `${current} -> ${next}` : `${current} -> 下一航班`;
    }
    if (type === "airport_discontinuity" || type === "AIRPORT_DISCONTINUITY" || type === "H2_CONNECTIVITY" || isTimeOverlapViolationType(type)) {
        const previous = violation?.previous_flight_number || violation?.prev_flight_number || violation?.previous_flight_id || violation?.prev_flight_id || "前序航班";
        const next = formatFlightSummary(row) || violation?.next_flight_number || violation?.next_flight_id || violation?.flight_id || "后续航班";
        return `${previous} -> ${next}`;
    }

    const flight = violation?.flight && typeof violation.flight === "object" ? violation.flight : null;
    return formatFlightSummary(flight) || formatFlightSummary(row) || violation?.flight_id || null;
}

function getViolationActualMinutes(violation, row) {
    return normalizeMinutes(
        violation?.actual_ground_time_minutes
        ?? violation?.actual_minutes
        ?? violation?.actual
        ?? row?.groundTimeMinutes,
    );
}

function getViolationRequiredMinutes(violation, row) {
    return normalizeMinutes(
        violation?.required_ground_time_minutes
        ?? violation?.required_minutes
        ?? violation?.required
        ?? row?.minGroundTimeMinutes,
    );
}

function formatGroundTimeDetail(violation, row) {
    if (isMissingGroundTimeConfigViolation(violation)) {
        return violation?.message || "缺少最小过站配置，无法校验当前航班到下一航班的过站要求。";
    }

    const actual = getViolationActualMinutes(violation, row);
    const required = getViolationRequiredMinutes(violation, row);
    if (actual !== null && required !== null) {
        return `过站不足：当前航班到下一航班实际 ${formatMinutesAsHHMM(actual)}，要求 ${formatMinutesAsHHMM(required)}。影响下一航班起飞，可从当前航班生成计算预览。`;
    }
    return violation?.message || "过站不足：当前航班到下一航班的 GT 低于要求，可能影响下一航班起飞。";
}

function formatActualRequiredLine(violation, row) {
    const type = getConstraintViolationType(violation);
    const actual = getViolationActualMinutes(violation, row);
    const required = getViolationRequiredMinutes(violation, row);
    if (isAutoCalculationDelayType(type)) {
        const delayMinutes = normalizeMinutes(violation?.delay_minutes ?? violation?.delayMinutes);
        return delayMinutes === null ? null : `推迟分钟数：${delayMinutes} 分钟`;
    }
    if (isTimeOverlapViolationType(type)) {
        const overlapMinutes = normalizeMinutes(violation?.overlap_minutes ?? violation?.actual_minutes ?? violation?.actual);
        return overlapMinutes === null ? null : `重叠分钟数：${overlapMinutes} 分钟`;
    }
    if (isNearCurfewViolationType(type)) {
        const minutesToCurfew = normalizeMinutes(violation?.minutes_to_curfew ?? violation?.minutesToCurfew);
        return minutesToCurfew === null ? null : `距宵禁开始：${minutesToCurfew} 分钟`;
    }
    if (isMissingGroundTimeConfigViolation(violation)) {
        return `实际值 / 要求值：${actual === null ? "--" : formatMinutesAsHHMM(actual)} / 未配置`;
    }
    if (isGroundTimeViolationType(type) && actual !== null && required !== null) {
        return `实际值 / 要求值：${formatMinutesAsHHMM(actual)} / ${formatMinutesAsHHMM(required)}`;
    }
    if (violation?.actual != null || violation?.required != null) {
        return `实际值 / 要求值：${violation?.actual ?? "--"} / ${violation?.required ?? "--"}`;
    }
    return null;
}

function formatUtcBasisLine(violation, row) {
    const type = getConstraintViolationType(violation);
    if (isAutoCalculationDelayType(type)) {
        const originalTime = violation?.original_time_utc || violation?.originalTimeUtc;
        const newTime = violation?.new_time_utc || violation?.newTimeUtc;
        const originalStd = violation?.original_std || violation?.originalStd;
        const newStd = violation?.new_std || violation?.newStd;
        const parts = [
            originalTime ? `规则触发前 ${originalTime}` : null,
            newTime ? `规则调整后 ${newTime}` : null,
            originalStd ? `原 STD ${originalStd}` : null,
            newStd ? `新 STD ${newStd}` : null,
        ].filter(Boolean);
        return parts.length ? `UTC 时间口径：${parts.join("；")}` : "UTC 时间口径：UTC";
    }
    if (isTimeOverlapViolationType(type)) {
        const previousSta = violation?.previous_sta_utc || violation?.previousStaUtc;
        const nextStd = violation?.next_std_utc || violation?.nextStdUtc;
        const parts = [previousSta ? `前序 STA ${previousSta}` : null, nextStd ? `后续 STD ${nextStd}` : null].filter(Boolean);
        return parts.length ? `UTC 时间口径：${parts.join("；")}` : "UTC 时间口径：UTC";
    }
    if (isGroundTimeViolationType(type)) {
        const sta = row?.rawFlight?.sta || row?.sta;
        const nextStd = row?.nextFlightSummary?.std;
        const parts = [sta ? `当前 STA ${sta}` : null, nextStd ? `下一 STD ${nextStd}` : null].filter(Boolean);
        return parts.length ? `UTC 时间口径：${parts.join("；")}` : "UTC 时间口径：UTC";
    }
    if (violation?.time_basis || violation?.flight_time_utc || violation?.flight_time) {
        return `UTC 时间口径：${violation.flight_time_utc || violation.flight_time || violation.time_basis}`;
    }
    return null;
}

function formatBusinessDetail(violation, row) {
    const type = getConstraintViolationType(violation);
    if (isAutoCalculationDelayType(type)) {
        const delayMinutes = normalizeMinutes(violation?.delay_minutes ?? violation?.delayMinutes);
        const airport = violation?.airport || "机场";
        const reason = violation?.reason === "curfew" ? `${airport} 宵禁` : (violation?.description || "自动计算规则");
        return violation?.business_message || violation?.message || `自动计算因 ${reason} 顺延${delayMinutes === null ? "" : ` ${delayMinutes} 分钟`}。`;
    }
    if (isTimeOverlapViolationType(type)) {
        return violation?.business_message || violation?.message || "前序航班 STA 晚于后续航班 STD，页面相邻航班时间重叠。";
    }
    if (isGroundTimeViolationType(type)) {
        return formatGroundTimeDetail(violation, row);
    }
    return violation?.business_message || violation?.message || formatConstraintViolation(violation);
}

function getAutoCalculateState(row, options = {}) {
    const rowFlightId = row?.rawFlight?.id || row?.id;
    const isDeleted = String(row?.rawFlight?.change_status || row?.changeStatus || "").toUpperCase() === "DELETED";
    const isAutoCalculating = rowFlightId != null && String(options.autoCalculatingFlightId || "") === String(rowFlightId);
    let reason = "";
    if (!options.canUpdateFlight) {
        reason = "当前轨只读，不能生成计算预览。";
    } else if (isDeleted) {
        reason = "已删除航班不能生成计算预览。";
    } else if (!rowFlightId) {
        reason = "当前行缺少可计算航班上下文。";
    } else if (!options.onAutoCalculate) {
        reason = "当前视图未提供计算预览入口。";
    } else if (options.autoCalculateDisabledReason) {
        reason = options.autoCalculateDisabledReason;
    }
    return {
        disabled: Boolean(reason),
        isAutoCalculating,
        reason,
    };
}

function renderConstraintPopoverContent(row, violations, options = {}) {
    const autoCalculateState = getAutoCalculateState(row, options);
    return (
        <div className="schedule-draft-rdg-constraint-popover" onMouseDown={(event) => event.stopPropagation()}>
            <div className="schedule-draft-rdg-constraint-popover-header">
                <Text strong>约束与提示详情</Text>
                <Tag>{violations.length} 项</Tag>
            </div>
            <div className="schedule-draft-rdg-constraint-popover-list">
                {violations.map((violation, index) => {
                    const action = violation?.next_action || violation?.suggestion;
                    return (
                        <div key={`${getConstraintViolationType(violation) || "violation"}-${index}`} className="schedule-draft-rdg-constraint-popover-item">
                            <div className="schedule-draft-rdg-constraint-popover-item-header">
                                <Text strong>{getConstraintViolationLabel(violation)}</Text>
                                <Space size={4} wrap>
                                    <Tag color={getConstraintSeverityTagColor(violation)}>{getConstraintSeverityLabel(violation)}</Tag>
                                    <Tag>{getConstraintSourceLabel(violation)}</Tag>
                                </Space>
                            </div>
                            <Text>{formatBusinessDetail(violation, row)}</Text>
                            {formatActualRequiredLine(violation, row) ? <Text type="secondary">{formatActualRequiredLine(violation, row)}</Text> : null}
                            {formatFlightPair(row, violation) ? <Text type="secondary">{`机场/航班对：${formatFlightPair(row, violation)}`}</Text> : null}
                            {formatUtcBasisLine(violation, row) ? <Text type="secondary">{formatUtcBasisLine(violation, row)}</Text> : null}
                            {formatRuleConditionsLine(violation) ? <Text type="secondary">{formatRuleConditionsLine(violation)}</Text> : null}
                            {action ? <Text type="secondary">{`建议：${action}`}</Text> : null}
                        </div>
                    );
                })}
            </div>
            <div className="schedule-draft-rdg-constraint-popover-actions">
                <Button
                    size="small"
                    type="primary"
                    icon={<CalculatorOutlined />}
                    disabled={autoCalculateState.disabled}
                    loading={autoCalculateState.isAutoCalculating}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!autoCalculateState.disabled) {
                            options.onAutoCalculate?.(row);
                        }
                    }}
                >
                    {autoCalculateState.isAutoCalculating ? "生成中" : "生成计算预览"}
                </Button>
                {autoCalculateState.reason ? <Text type="secondary">{autoCalculateState.reason}</Text> : <Text type="secondary">只生成预览，不会直接保存或静默应用。</Text>}
            </div>
        </div>
    );
}

function formatConstraintViolation(violation) {
    if (!violation) {
        return "未知约束违规";
    }

    const violationType = getConstraintViolationType(violation);

    if (isAutoCalculationDelayType(violationType)) {
        const delayMinutes = normalizeMinutes(violation?.delay_minutes ?? violation?.delayMinutes);
        return violation?.message || `自动顺延${delayMinutes === null ? "" : ` ${delayMinutes} 分钟`}`;
    }

    if (isTimeOverlapViolationType(violationType)) {
        const overlapMinutes = normalizeMinutes(violation?.overlap_minutes ?? violation?.actual_minutes ?? violation?.actual);
        return violation?.message || `时间重叠${overlapMinutes === null ? "" : `：${overlapMinutes} 分钟`}`;
    }

    if (isNearCurfewViolationType(violationType)) {
        const minutesToCurfew = normalizeMinutes(violation?.minutes_to_curfew ?? violation?.minutesToCurfew);
        return violation?.message || `临近宵禁${minutesToCurfew === null ? "" : `：距宵禁开始 ${minutesToCurfew} 分钟`}`;
    }

    if (LOCAL_CONSTRAINT_LABELS[violationType]) {
        return violation?.message ? String(violation.message) : LOCAL_CONSTRAINT_LABELS[violationType];
    }

    if (violationType === "dep_curfew" || violationType === "arr_curfew") {
        const direction = violationType === "dep_curfew" ? "DEP" : "ARR";
        return `${direction} 宵禁违规：${violation.airport || "--"} ${formatCurfewWindow(violation.curfew_window)}`;
    }

    if (isMissingGroundTimeConfigViolation(violation)) {
        return "缺少最小过站配置，无法校验过站要求";
    }

    if (isGroundTimeViolationType(violationType)) {
        const actual = violation.actual_ground_time_minutes ?? violation.actual_minutes ?? violation.actual;
        const required = violation.required_ground_time_minutes ?? violation.required_minutes ?? violation.required;
        if (actual != null && required != null) {
            return `过站时间不足：实际 ${actual}min，要求 ${required}min`;
        }
        return "过站时间不足";
    }

    if (violationType === "missing_turnaround_data") {
        return "缺少过站时间数据";
    }

    if (BACKEND_CONSTRAINT_LABELS[violationType]) {
        return violation?.message ? String(violation.message) : BACKEND_CONSTRAINT_LABELS[violationType];
    }

    return violation?.message ? String(violation.message) : (violationType || "未知约束违规");
}

function renderConstraintStatus(violations = [], row = null, options = {}) {
    if (!violations.length) {
        if (row?.aircraftAvailabilityValidationPending) {
            return <span className="schedule-draft-rdg-constraint-status schedule-draft-rdg-constraint-status-info" title="飞机可用性窗口尚未可用，暂不判定为正常">待确认</span>;
        }
        return <span className="schedule-draft-rdg-constraint-status schedule-draft-rdg-constraint-status-ok">正常</span>;
    }

    const status = (
        <span
            className="schedule-draft-rdg-constraint-status"
            aria-label={`约束与提示：${violations.map(getConstraintViolationLabel).join("，")}`}
        >
            {violations.map((violation, index) => (
                <span
                    key={`${getConstraintViolationType(violation) || "violation"}-${index}`}
                    className={`schedule-draft-rdg-constraint-status-text ${getConstraintSeverityClass(violation)}`}
                    title={formatConstraintViolation(violation)}
                >
                    {getConstraintStatusLabel(violation)}
                </span>
            ))}
        </span>
    );

    return (
        <Popover
            trigger={["hover", "focus"]}
            placement="rightTop"
            autoAdjustOverflow
            mouseEnterDelay={0}
            content={renderConstraintPopoverContent(row, violations, options)}
        >
            <button
                type="button"
                className="schedule-draft-rdg-constraint-status-trigger"
                aria-label={`约束与提示详情：${violations.map(getConstraintViolationLabel).join("，")}`}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
            >
                {status}
            </button>
        </Popover>
    );
}

function getGroundTimeViolation(violations = []) {
    return violations.find((violation) => isGroundTimeViolationType(getConstraintViolationType(violation)));
}

function getEffectiveGroundTimeViolation(row) {
    if (Object.prototype.hasOwnProperty.call(row || {}, "groundTimeViolation")) {
        return row?.groundTimeViolation || null;
    }

    return getGroundTimeViolation(row?.constraintViolations);
}

function getEffectiveConstraintViolations(row) {
    return row?.constraintViolations || [];
}

function renderReadonlyTimeCell(record, direction, useEffectiveTime) {
    const isDeparture = direction === "departure";
    const value = isDeparture ? record.std : record.sta;
    const airportCode = isDeparture ? record.departureAirport : record.arrivalAirport;
    const sourceLabel = isDeparture ? record.departureTimeSource : record.arrivalTimeSource;
    const scheduledLabel = isDeparture ? "STD" : "STA";
    const scheduledValue = isDeparture ? record.scheduledStd : record.scheduledSta;
    const baseTime = isDeparture ? null : record.std;
    const scheduledBaseTime = isDeparture ? null : record.scheduledStd || record.std;
    const showOriginal = Boolean(useEffectiveTime && sourceLabel !== scheduledLabel && scheduledValue);
    const tooltipTitle = useEffectiveTime
        ? (
            <Space direction="vertical" size={2}>
                <Text>{`${isDeparture ? "STD/ETD" : "STA/ETA"} 来源：${sourceLabel}`}</Text>
                {showOriginal ? (
                    <Text>
                        原 {scheduledLabel}：<FlightTimeDisplay utcTime={scheduledValue} airportCode={airportCode} baseUtcTime={scheduledBaseTime} stdTime={scheduledBaseTime} showDate disableDetail />
                    </Text>
                ) : null}
                <Text type="secondary">ATD/ATA 不参与编排定位</Text>
            </Space>
        )
        : null;
    const content = (
        <Space size={4} wrap>
            <Text>
                <FlightTimeDisplay utcTime={value} airportCode={airportCode} baseUtcTime={baseTime} stdTime={baseTime} showDate disableDetail />
            </Text>
            {useEffectiveTime ? <Tag>{sourceLabel}</Tag> : null}
        </Space>
    );

    return tooltipTitle ? <Tooltip title={tooltipTitle}>{content}</Tooltip> : content;
}

/**
 * 参考轨只读 Antd Table 列定义。
 */
export function buildReadonlyColumns({ useEffectiveTime = false } = {}) {
    return [
        { title: "航班号", dataIndex: "flightNumber", key: "flightNumber", width: 120, fixed: "left" },
        { title: "飞机", dataIndex: "aircraftReg", key: "aircraftReg", width: 140 },
        {
            title: "航线",
            key: "route",
            width: 160,
            render: (_, record) => `${record.departureAirport} -> ${record.arrivalAirport}`,
        },
        {
            title: "航路",
            key: "flightRoute",
            width: 180,
            render: (_, record) =>
                record.flightRouteId ? (
                    <Space size={4} wrap>
                        <Text>{record.flightRouteFullName || record.flightRouteName || record.flightRouteSummary || `Route #${record.flightRouteId}`}</Text>
                        <Tag>{formatBlockTime(record.flightRouteBlockTimeMinutes)}</Tag>
                    </Space>
                ) : (
                    <Tag color="warning">未绑定</Tag>
                ),
        },
        {
            title: useEffectiveTime ? "STD/ETD" : "STD",
            dataIndex: "std",
            key: "std",
            width: 180,
            render: (_, record) => renderReadonlyTimeCell(record, "departure", useEffectiveTime),
        },
        {
            title: useEffectiveTime ? "STA/ETA" : "STA",
            dataIndex: "sta",
            key: "sta",
            width: 180,
            render: (_, record) => renderReadonlyTimeCell(record, "arrival", useEffectiveTime),
        },
        {
            title: "Block Hour",
            key: "blockTime",
            width: 100,
            render: (_, record) => (
                <TimeBarCell
                    actualMinutes={record.blockTimeMinutes}
                    minMinutes={record.minBlockTimeMinutes}
                    actualLabel="实际轮档时间"
                    minLabel="最小轮档时间"
                    previewDiff={record.previewDiffs?.block_time_minutes}
                    showBar={false}
                />
            ),
        },
        {
            title: "Ground Time",
            key: "groundTime",
            width: 110,
            render: (_, record) => (
                <TimeBarCell
                    actualMinutes={record.groundTimeMinutes}
                    minMinutes={record.minGroundTimeMinutes}
                    actualLabel="实际过站时间"
                    minLabel="最小过站时间"
                    violation={getEffectiveGroundTimeViolation(record)}
                    previewDiff={record.previewDiffs?.ground_time_minutes}
                    showViolationTag
                />
            ),
        },
        {
            title: "约束与提示",
            key: "constraintStatus",
            width: 120,
            render: (_, record) => renderConstraintStatus(getEffectiveConstraintViolations(record), record, { canUpdateFlight: false }),
        },
    ].map(applyReadonlyColumnAlignment);
}

export const readonlyColumns = buildReadonlyColumns();

/**
 * 构建当前草稿可编辑 react-data-grid 列定义。
 * @param {Object} params - 列行为参数。
 * @param {boolean} params.canUpdateFlight - 是否允许编辑航班字段。
 * @param {boolean} params.canDeleteFlight - 是否允许删除航班。
 * @param {boolean} params.deletingFlight - 删除 mutation loading 状态。
 * @param {(flight: Object) => void} params.onOpenEdit - 打开弹窗编辑的回调。
 * @param {(flightId: string|number) => Promise<*>} params.onDeleteFlight - 删除航班回调。
 * @returns {Array<Object>} react-data-grid 列定义。
 */
export function buildEditableColumns({
    canUpdateFlight,
    canDeleteFlight,
    deletingFlight,
    onOpenEdit,
    onDeleteFlight,
    onAutoCalculate,
    autoCalculatingFlightId,
    autoCalculateDisabledReason,
    getFlightNumberSuggestion,
    loadFlightNumberSuggestion,
    activeRouteCellKey,
    onOpenRouteSearch,
    onCloseRouteSearch,
    onSelectRoute,
    activeActionRowId,
    onActiveActionRowChange,
    onAddFlightAtRow,
}) {
    const isDeletedRow = (row) => String(row?.rawFlight?.change_status || row?.changeStatus || "").toUpperCase() === "DELETED";
    const openDeleteConfirm = (row) => {
        const flightId = row.rawFlight?.id;
        if (!flightId || !canDeleteFlight || isDeletedRow(row)) {
            return;
        }

        Modal.confirm({
            title: `确认删除航班 ${row.flightNumber || "未命名航班"}`,
            content: "删除会先标记到本地编辑，可在保存前通过撤销恢复。",
            okText: "确认删除",
            cancelText: "取消",
            okButtonProps: { danger: true },
            autoFocusButton: "ok",
            keyboard: true,
            onOk: () => onDeleteFlight(flightId),
        });
    };

    const columns = [
        {
            key: "aircraftReg",
            name: "Aircraft Reg.",
            width: 150,
            renderCell: AircraftCellRenderer,
            renderEditCell: renderAircraftEditCell,
            editable: canUpdateFlight,
        },
        {
            key: "flightDate",
            name: "Date",
            width: 80,
            renderCell: FlightDateCellRenderer,
        },
        {
            key: "dayOfWeek",
            name: "D",
            width: 40,
            renderCell: DayOfWeekCellRenderer,
        },
        {
            key: "flightNumber",
            name: "Flight No.",
            width: 120,
            renderEditCell: renderFlightNumberEditCell,
            getFlightNumberSuggestion,
            loadFlightNumberSuggestion,
            editable: canUpdateFlight,
        },
        {
            key: "departureAirport",
            name: "DEP",
            width: 60,
            renderCell: AirportCellRenderer,
            renderEditCell: renderAirportEditCell,
            editable: canUpdateFlight,
        },
        {
            key: "arrivalAirport",
            name: "ARR",
            width: 60,
            renderCell: AirportCellRenderer,
            renderEditCell: renderAirportEditCell,
            editable: canUpdateFlight,
        },
        {
            key: "flightRouteId",
            name: "Route",
            width: 180,
            minWidth: 140,
            renderCell: (props) => (
                <RouteCellRenderer
                    {...props}
                    canUpdateFlight={canUpdateFlight}
                    isRouteSearchOpen={activeRouteCellKey === props.row?.id}
                    onOpenRouteSearch={onOpenRouteSearch}
                    onCloseRouteSearch={onCloseRouteSearch}
                    onSelectRoute={onSelectRoute}
                />
            ),
        },
        {
            key: "stdTime",
            name: "STD",
            width: 100,
            renderCell: FlightTimeCellRenderer,
            renderEditCell: renderFlightTimeEditCell,
            editorOptions: { commitOnOutsideClick: false },
            editable: canUpdateFlight,
        },
        {
            key: "staTime",
            name: "STA",
            width: 100,
            renderCell: FlightTimeCellRenderer,
            renderEditCell: renderFlightTimeEditCell,
            editorOptions: { commitOnOutsideClick: false },
            editable: canUpdateFlight,
        },
        {
            key: "blockTime",
            name: "Block Hour",
            width: 100,
            renderCell: ({ row }) => (
                <TimeBarCell
                    actualMinutes={row.blockTimeMinutes}
                    minMinutes={row.minBlockTimeMinutes}
                    actualLabel="实际轮档时间"
                    minLabel="最小轮档时间"
                    previewDiff={row.previewDiffs?.block_time_minutes}
                    showViolationTag
                    showBar={false}
                />
            ),
        },
        {
            key: "groundTime",
            name: "Ground Time",
            width: 110,
            renderCell: ({ row }) => (
                <TimeBarCell
                    actualMinutes={row.groundTimeMinutes}
                    minMinutes={row.minGroundTimeMinutes}
                    actualLabel="实际过站时间"
                    minLabel="最小过站时间"
                    violation={getEffectiveGroundTimeViolation(row)}
                    previewDiff={row.previewDiffs?.ground_time_minutes}
                    showViolationTag
                />
            ),
        },
        {
            key: "stcType",
            name: "STC",
            width: 90,
            renderCell: ({ row }) => <span className="schedule-draft-rdg-stc-cell" style={centerCellStyle}>{row.stcType || ""}</span>,
            renderEditCell: renderSTCEditCell,
            editable: canUpdateFlight,
        },
        {
            key: "remarks",
            name: "Remarks",
            width: 120,
            renderCell: RemarksCellRenderer,
            renderEditCell: renderRemarksEditCell,
            editable: canUpdateFlight,
        },
        {
            key: "constraintStatus",
            name: "约束与提示",
            width: 110,
            renderCell: ({ row }) => renderConstraintStatus(getEffectiveConstraintViolations(row), row, {
                canUpdateFlight,
                onAutoCalculate,
                autoCalculatingFlightId,
                autoCalculateDisabledReason,
            }),
        },
        {
            name: "Actions",
            key: "actions",
            width: 260,
            renderCell: ({ row }) => {
                const rowId = row?.id || row?.rawFlight?.id;
                const isActive = rowId != null && String(activeActionRowId || "") === String(rowId);
                const isDeleted = isDeletedRow(row);
                const isAutoCalculating = String(autoCalculatingFlightId || "") === String(row.rawFlight?.id || "");
                const autoCalculateDisabled = isDeleted || Boolean(autoCalculateDisabledReason) || !canUpdateFlight || !row.rawFlight?.id;
                const addFlightDisabled = isDeleted || !canUpdateFlight || !row.rawFlight?.id || !onAddFlightAtRow;

                return (
                    <span
                        className="schedule-draft-rdg-actions"
                        onMouseEnter={() => onActiveActionRowChange?.(rowId)}
                        onMouseLeave={() => onActiveActionRowChange?.(null)}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {!isActive ? (
                            <button
                                type="button"
                                className="schedule-draft-rdg-actions-placeholder"
                                title="显示操作"
                                onFocus={() => onActiveActionRowChange?.(rowId)}
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onActiveActionRowChange?.(rowId);
                                }}
                            >
                                ...
                            </button>
                        ) : (
                            <>
                                {onAutoCalculate && (
                                    <TimetableActionButton
                                        disabled={autoCalculateDisabled}
                                        title={autoCalculateDisabledReason || "从本行开始，按宵禁、过站、运输需求向后计算"}
                                        onClick={() => onAutoCalculate(row)}
                                    >
                                        <CalculatorOutlined /> {isAutoCalculating ? "计算中" : "计算"}
                                    </TimetableActionButton>
                                )}
                                <TimetableActionButton disabled={addFlightDisabled} title="在上方添加航班" onClick={() => onAddFlightAtRow?.(row, "before")}>
                                    <PlusOutlined /> 上方
                                </TimetableActionButton>
                                <TimetableActionButton disabled={addFlightDisabled} title="在下方添加航班" onClick={() => onAddFlightAtRow?.(row, "after")}>
                                    <PlusOutlined /> 下方
                                </TimetableActionButton>
                                <TimetableActionButton disabled={isDeleted || !canUpdateFlight} title="编辑航班" onClick={() => onOpenEdit(row.rawFlight)}>
                                    编辑
                                </TimetableActionButton>
                                <TimetableActionButton danger disabled={isDeleted || !canDeleteFlight || deletingFlight} title="删除航班" onClick={() => openDeleteConfirm(row)}>
                                    删除
                                </TimetableActionButton>
                            </>
                        )}
                    </span>
                );
            },
        },
    ];

    return columns.map((column) => {
        const alignedColumn = applyEditableColumnAlignment(column);
        const renderCell = alignedColumn.renderCell;

        if (!renderCell) {
            return alignedColumn;
        }

        return {
            ...alignedColumn,
            renderCell: (props) => {
                if (props.row?._rowType === "rotation-separator") {
                    return null;
                }
                if (props.row?._rowType === "change-preview" && alignedColumn.key === "actions") {
                    return null;
                }
                return renderCell(props);
            },
        };
    });
}
